import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

const PENDING_PLAID_OAUTH_KEY = 'plaid.pendingOAuth';

export interface PendingPlaidOAuthState {
  linkToken: string;
  returnPath: string;
}

function getWebStorage() {
  if (typeof localStorage === 'undefined') return null;
  if (typeof localStorage.getItem !== 'function') return null;
  if (typeof localStorage.setItem !== 'function') return null;
  if (typeof localStorage.removeItem !== 'function') return null;
  return localStorage;
}

function normalizeReturnPath(path: string | null | undefined): string {
  if (!path || !path.startsWith('/') || path.startsWith('//')) {
    return '/(app)/accounts';
  }

  return path;
}

function parsePendingPlaidOAuthState(
  raw: string | null | undefined,
): PendingPlaidOAuthState | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PendingPlaidOAuthState>;
    if (
      typeof parsed.linkToken !== 'string' ||
      parsed.linkToken.length === 0 ||
      typeof parsed.returnPath !== 'string'
    ) {
      return null;
    }

    return {
      linkToken: parsed.linkToken,
      returnPath: normalizeReturnPath(parsed.returnPath),
    };
  } catch {
    return null;
  }
}

export async function setPendingPlaidOAuthState(
  state: PendingPlaidOAuthState | null,
): Promise<void> {
  if (!state) {
    await clearPendingPlaidOAuthState();
    return;
  }

  const serialized = JSON.stringify({
    linkToken: state.linkToken,
    returnPath: normalizeReturnPath(state.returnPath),
  });

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.setItem(PENDING_PLAID_OAUTH_KEY, serialized);
    return;
  }

  await SecureStore.setItemAsync(PENDING_PLAID_OAUTH_KEY, serialized);
}

export async function getPendingPlaidOAuthState(): Promise<PendingPlaidOAuthState | null> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    return parsePendingPlaidOAuthState(
      storage?.getItem(PENDING_PLAID_OAUTH_KEY) ?? null,
    );
  }

  return parsePendingPlaidOAuthState(
    await SecureStore.getItemAsync(PENDING_PLAID_OAUTH_KEY),
  );
}

export async function clearPendingPlaidOAuthState(): Promise<void> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.removeItem(PENDING_PLAID_OAUTH_KEY);
    return;
  }

  await SecureStore.deleteItemAsync(PENDING_PLAID_OAUTH_KEY);
}
